import { ApplicationState } from "../store";
import { InsurancePeriod } from "../store/Insurance";
import {
  DataLayerProps,
  EnvironmentInfoProps,
  PageInfoProps,
  CustomerInfoProps,
  ProductInfoProps,
  ProcessInfoProps,
  ObjectInfoProps,
  InsuranceInfoProps,
  EcommerceProps,
  ILocalValidation,
} from "./";
import { GetSelectedAddonsForCoverage } from "../store/AddOn";
import { format } from "date-fns";
import { BrandsEnum, GetBrandArea } from "../enums";
import { GetIntFromString } from "../helperFunctions";

function getEntranceType(data: ApplicationState): string {
  const isRemote = data.inputs?.webshopMode === 2 || data.inputs?.isRemote === true ? true : false;
  const isKamux = !!data.inputs?.isKamux;
  const isAfPris = !!data.inputs?.isAfpris;
  const isRullavidare = !isKamux && !isAfPris && (data.inputs?.partner === BrandsEnum.IfRullaVidare || data.inputs?.partner === BrandsEnum.IfRullaVidareMO);
  const isRullaUt = !!data.insurance?.startDate?.isDisabled && !isRullavidare;
  const isDealerPrice = !!data.dealerPrice?.IsDealerPrice;

  if (isRemote) return "remote";
  if (isKamux) return "kamux";
  if (isAfPris) return "afpris";
  if (isDealerPrice) return "dealerprice";
  if (isRullavidare) return "rullavidare";
  if (isRullaUt) return "rullaut";
  return "null";
}

function getEnvInfo(data: ApplicationState): EnvironmentInfoProps {
  const env = data.profile;
  const inputs = data.inputs;
  return {
    country: data.inputs.country,
    businessArea: data.inputs.isCommercial ? "COM" : "PRI",
    portfolio: env?.portfolio ?? GetBrandArea(inputs.partner),
    partner: inputs?.partner,
  };
}

function getPageInfo(data: ApplicationState): PageInfoProps {
  return {
    pageType: "Webshop Page",
    pageLanguage: data.inputs.language,
    pageUrl: window.location.href,
    canonicalPageUrl: undefined,
    httpStatusCode: "200",
  };
}

function getCustomerInfo(data: ApplicationState): CustomerInfoProps {
  const customer = data.profile;
  const damageFree = data.damageFreeYear;
  const insuranceReceive = data.insurance?.insuranceReceive;

  let isECustomer = customer?.isECustomer == null ? undefined : customer.isECustomer;
  if (insuranceReceive) {
    isECustomer = insuranceReceive.selectedValue == null ? undefined : insuranceReceive.selectedValue === "W";
  }

  return {
    customerId: {
      id: customer?.encryptedSSN,
      type: customer?.age && customer?.age > 0 ? "SSN" : "ORG",
    },
    age: customer?.age,
    gender: customer?.gender,
    customerStatus: [
      {
        type: "if",
        value: "Unknown",
      },
      {
        type: "carPartners",
        value: "Unknown",
      },
    ],
    customerDiscounts: undefined,
    isObjectPossessor: undefined,
    loggedIn: false,
    campaignCode: customer?.campaignId,
    fullname: customer?.encryptedName,
    firstname: undefined,
    lastname: undefined,
    recentClaims: damageFree?.selected == null ? undefined : !damageFree.selected ? "Yes" : "No",
    eCustomer: isECustomer ? "Yes" : "No",
    phone: customer?.phone?.secureValue,
    email: customer?.email?.secureValue,
  };
}

function getProductInfo(): ProductInfoProps {
  return {
    productName: {
      localName: "Personbil",
      localSubname: undefined,
      name: "Passenger Car",
      subname: undefined,
    },
    currencyCode: "SEK",
  };
}

function getProcessInfo(data: ApplicationState, mainStepName: string): ProcessInfoProps {
  let status: "available" | "not available" = "not available";
  const inputs = data.inputs;

  const name: ProcessInfoProps["name"] = "webshop";

  if (inputs?.partner === BrandsEnum.Polestar) {
    status = inputs?.redirectLink ? "available" : "not available";
  }

  return {
    name,
    processStep: {
      mainStep: mainStepName as any,
      subStep: undefined,
      status,
    },
  };
}

function getObjectInfo(data: ApplicationState): ObjectInfoProps {
  const obj = data.profile;
  const inputs = data.inputs;
  const isOrg = inputs && inputs?.orn && inputs?.orn != null;
  const calcByModel =
    inputs?.partner === BrandsEnum.Polestar &&
    !isOrg &&
    (!obj?.encryptedRegistrationNumber || obj?.encryptedRegistrationNumber === "undefined");
  return {
    objectId: {
      id: obj?.encryptedRegistrationNumber === "undefined" ? "undefined" : obj?.encryptedRegistrationNumber,
      type: calcByModel ? "" : "REG",
    },
    objectDetails: [
      { type: "isElectric", value: obj?.isCarBatteryElectric ?? false },
      { type: "eFlag", value: obj?.isCarVolvoBatteryElectric ?? false },
    ],
    model: obj?.carModel,
    year: obj?.carYear,
    objectSize: {
      value: undefined,
      unit: "kg",
    },
  };
}

function getInsuranceInfo(data: ApplicationState): InsuranceInfoProps {
  const inputs = data.inputs;
  const insurance = data.insurance;
  const distance = data.distance;
  const addons = data.addon;
  const youngDriver = data.youngDriver;
  const payment = data.payment;
  const dealerPrice = data.dealerPrice;

  const selectedCoverage = insurance?.coverage?.Coverages?.find(elem => elem.InternalCode === insurance.coverage?.selectedCode);

  const preSelectedDeductible =
    insurance?.coverage?.SelectedDeductible ?? selectedCoverage?.Deductibles?.find(d => d.IsSelected === true)?.Code;

  const preSelectedGlassDeductible =
    insurance?.coverage?.SelectedGlassDeductible ?? selectedCoverage?.GlassDeductibles?.find(d => d.IsSelected === true)?.Code;

  const primaryPrice = selectedCoverage?.PrimaryPrice;
  const secondaryPrice = selectedCoverage?.SecondaryPrice;
  const insurancePrice: number = GetYearlyPrice(primaryPrice?.Period, primaryPrice?.Amount, secondaryPrice?.Amount);

  // // start of dataLayer
  const krRegular = / kr/gi;
  const insuranceDeductable =
    preSelectedDeductible && selectedCoverage?.Deductibles
      ? Number(selectedCoverage.Deductibles.find(d => d.Code === preSelectedDeductible)?.Name.replace(krRegular, ""))
      : undefined;

  const glassDeductible =
    preSelectedGlassDeductible && selectedCoverage?.GlassDeductibles
      ? Number(selectedCoverage?.GlassDeductibles?.find(d => d.Code === preSelectedGlassDeductible)?.Name.replace(krRegular, ""))
      : undefined;

  let selectedPayment = payment?.paymentTypes?.find(option => option.selected);
  if (payment?.selectedPaymentTypeCode) {
    selectedPayment = payment?.paymentTypes?.find(
      option =>
        option.code === payment?.selectedPaymentTypeCode ||
        option.paymentPeriods?.find(subOption => subOption.Code === payment?.selectedPaymentTypeCode),
    );
  }

  let paymentType: undefined | "volvocard" | "paperinvoice" | "einvoice" | "directdebit" = undefined;
  // detect payment type based on selected methods code
  if (payment?.selectedPaymentTypeCode) {
    const code = payment.selectedPaymentTypeCode.toLowerCase();
    if (code.includes("pap")) {
      // paper
      paymentType = "paperinvoice";
    } else if (code.includes("avk")) {
      // volvocard or einvoice, based on period
      paymentType = code.includes("12") ? "volvocard" : "einvoice";
    } else if (code.includes("bga")) {
      paymentType = "directdebit";
    } else if (code.includes("ef")) {
      paymentType = "einvoice";
    }
  }
  // in case of unknown code, fallback to title-based logic
  if (!paymentType && selectedPayment?.title) {
    const paymentName = selectedPayment?.title.toLowerCase();
    paymentType = paymentName.includes("volvokort")
      ? "volvocard"
      : paymentName.includes("papper")
        ? "paperinvoice"
        : paymentName.includes("faktura")
          ? "einvoice"
          : "directdebit";
  }

  let paymentPeriod: undefined | "monthly" | "halfYearly" | "yearly" = undefined;
  if (payment?.selectedPaymentTypeCode) {
    switch (GetIntFromString(payment?.selectedPaymentTypeCode)) {
      case 1:
        paymentPeriod = "yearly";
        break;
      case 2:
        paymentPeriod = "halfYearly";
        break;
      case 12:
        paymentPeriod = "monthly";
        break;
    }
  }

  const selectedAddonsForCoverage = GetSelectedAddonsForCoverage(addons, insurance);
  const selectedAddons =
    selectedAddonsForCoverage
      .map(x => addons?.addOns?.find(elem => elem.InternalCode === x)?.Name)
      ?.filter(x => x != null)
      ?.map(x => ({ value: x })) ?? [];

  if (selectedAddons.length === 0) {
    selectedAddons.push({ value: "" });
  }

  function tryFormat(date: Date) {
    try {
      return format(date, "yyyy-MM-dd");
    } catch (error) {
      return undefined;
    }
  }

  const isRullaUt = data.insurance?.startDate.isDisabled ?? false;

  return {
    price: Number(insurancePrice),
    quoteId: isRullaUt ? "rullaut" : dealerPrice?.IsDealerPrice ? "reseller" : inputs?.quoteId,
    entranceType: getEntranceType(data),
    coverage: selectedCoverage?.LongName,
    insuranceType: {
      name: "fixed",
      movingPrice: undefined,
      fixedPrice: undefined,
    }, // insuranceType is only relevant for volvia. For any other webshops, insuranceType can be left out.
    insuranceDeductable: [
      {
        type: "product",
        value: insuranceDeductable,
      },
      {
        type: "glass",
        value: glassDeductible,
      },
    ],
    coveredAmount: undefined,
    mileage: distance?.values.find(mileage => mileage.value === distance.selected)?.title,
    insuranceAddons: selectedAddons,
    youngDriver:
      !youngDriver?.questionVisible || youngDriver?.selected === null || youngDriver?.selected === undefined
        ? undefined
        : youngDriver?.values
          .find(state => state.value === youngDriver?.selected)
          ?.text.toLowerCase()
          .startsWith("n") !== true,

    startdate: insurance?.startDate?.value ? tryFormat(insurance?.startDate?.value) : undefined,
    insurancePayment: {
      invoice: paymentType,
      period: paymentPeriod,
    },
    signingId: data.signingService?.encryptedSigningId ?? undefined,
    ehdId: undefined,
  };
}

function getEcommerceInfo(data: ApplicationState, eventName: string): EcommerceProps {
  const env = getEnvInfo(data);
  const insurance = getInsuranceInfo(data);
  const product = getProductInfo();

  if (eventName.includes("Checkout Step")) {
    return {
      currencyCode: product.currencyCode,
      checkout: {
        actionField: {
          id: new Date().toDateString() + Math.random(),
          affiliation: env?.partner,
          revenue: insurance.price,
          action: "checkout",
        },
        products: [
          {
            id: product?.productName?.localName,
            name: product?.productName?.localName,
            price: insurance.price,
            brand: env?.portfolio,
            category: undefined,
            variant: insurance?.coverage,
            quantity: 1,
          },
        ],
      },
    };
  } else {
    return {
      currencyCode: product.currencyCode,
      purchase: {
        actionField: {
          id: new Date().toDateString() + Math.random(),
          affiliation: env?.partner,
          revenue: insurance.price,
          action: "purchase",
        },
        products: [
          {
            id: product?.productName?.localName,
            name: product?.productName?.localName,
            price: insurance.price,
            brand: env?.portfolio,
            category: undefined,
            variant: insurance?.coverage,
            quantity: 1,
          },
        ],
      },
    };
  }
}

function GetYearlyPrice(primaryPeriod: InsurancePeriod | undefined, primaryPrice: any, secondaryPrice: any) {
  switch (primaryPeriod) {
    case InsurancePeriod.year:
      return Number(primaryPrice) || secondaryPrice * 12;
    case InsurancePeriod.sixMonth:
      return Number(secondaryPrice) || primaryPrice * 2;
    default:
      return Number(secondaryPrice) || primaryPrice * 12;
  }
}

export function getPageLoadModel(data: ApplicationState, eventType = "Page Load", processMainStepName = "price"): DataLayerProps {
  return {
    event: eventType,
    environmentInfo: getEnvInfo(data),
    pageInfo: getPageInfo(data),
    customerInfo: getCustomerInfo(data),
    productInfo: getProductInfo(),
    processInfo: getProcessInfo(data, processMainStepName),
    objectInfo: getObjectInfo(data),
    insuranceInfo: getInsuranceInfo(data),
  };
}

export function getServerErrorModel(data: ApplicationState): DataLayerProps {
  const customerInfo = getCustomerInfo(data);
  return {
    event: "Web Shop Server Error",
    customerInfo: {
      customerId: customerInfo.customerId,
      age: customerInfo.age,
      gender: customerInfo.gender,
      customerStatus: customerInfo.customerStatus,
      customerDiscounts: customerInfo.customerDiscounts,
      isObjectPossessor: customerInfo.isObjectPossessor,
    },
    processInfo: getProcessInfo(data, "error"),
    objectInfo: getObjectInfo(data),
    insuranceInfo: {
      errorCode: "Server - 500",
    },
  };
}

//FORMAT STRING:
//FORMAT - email - 2 (1 - required, 2 - invalid)

export function getLocalValidationErrorModel(data: ApplicationState, validationError: ILocalValidation): DataLayerProps {
  const customerInfo = getCustomerInfo(data);
  return {
    event: "Web Shop Format Error",
    customerInfo: {
      customerId: customerInfo.customerId,
      age: customerInfo.age,
      gender: customerInfo.gender,
      customerStatus: customerInfo.customerStatus,
      customerDiscounts: customerInfo.customerDiscounts,
      isObjectPossessor: customerInfo.isObjectPossessor,
    },
    processInfo: getProcessInfo(data, "error"),
    objectInfo: getObjectInfo(data),
    insuranceInfo: {
      errorCode: `Format - ${validationError.fieldName} - ${validationError.state}`,
    },
  };
}

export function getDataUpdateModel(data: ApplicationState): DataLayerProps {
  return {
    event: "Insurance Information Updated",
    insuranceInfo: getInsuranceInfo(data),
    customerInfo: getCustomerInfo(data),
  };
}
export function getEcommerceModel(data: ApplicationState, eventName: string): DataLayerProps {
  return {
    event: eventName,
    ecommerce: getEcommerceInfo(data, eventName),
  };
}
